import { CommonModule, NgOptimizedImage } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { CSP_NONCE, ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { Router } from '@angular/router';
import { ServiceWorkerModule, SwUpdate } from '@angular/service-worker';
import { ErrorsModule } from '@app/pages/error-pages/errors.module';
import { environment } from '@environments/environment';
import { PublicGuard } from '@guard/public.guard';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';

import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { MyAppsModule } from '@pages/my-apps/my-apps.module';
import Lara from '@primeng/themes/lara';
import * as Sentry from '@sentry/angular';
import { AuthService } from '@services/auth.service';
import { NgxSpinnerModule } from 'ngx-spinner';
import { TimeagoModule } from 'ngx-timeago';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { ToastModule } from 'primeng/toast';
import { lastValueFrom } from 'rxjs';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthGuard } from './guard/auth.guard';
import { AuthInterceptor } from './interceptors/authconfig.interceptor';
import { SpacesModule } from './pages/spaces/spaces.module';
import { SharedModule } from './shared/share.module';
const nonce = (document.querySelector('meta[name="CSP_NONCE"]') as HTMLMetaElement)?.content;

export function initializeApp(authService: AuthService): () => Promise<boolean | void> {
  return () => lastValueFrom(authService.initialize());
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    SharedModule,
    NgxSpinnerModule,
    MatProgressSpinnerModule,
    TimeagoModule.forRoot(),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    MatTooltipModule,
    MatTabsModule,
    MatIconModule,
    ErrorsModule,
    SpacesModule,
    MyAppsModule,
    NgOptimizedImage,
    ToastModule,
  ],
  providers: [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'en',
    }),
    // Ensure translations are loaded before the app bootstraps
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      try {
        const browserLang = translate.getBrowserLang();
        const selected = browserLang && browserLang.match(/en|fr|es|ca|de/) ? browserLang : 'en';
        // translate.use returns an Observable; convert to Promise and return
        return lastValueFrom(translate.use(selected)).catch((e) => {
          console.error('Error initializing translations', e);
          return;
        });
      } catch (e) {
        console.error('Error initializing translations', e);
        return Promise.resolve();
      }
    }),
    {
      provide: CSP_NONCE,
      useValue: nonce,
    },
    AuthGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(async () => inject(Sentry.TraceService)),
    provideAppInitializer(() => {
      const initializerFn = initializeApp(inject(AuthService));
      return initializerFn();
    }),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: false || 'none',
          cssLayer: true,
        },
      },
    }),
    MessageService,
    PublicGuard,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(sw: SwUpdate) {
    sw.unrecoverable.subscribe(() => {
      console.error('Unrecoverable state, reloading the page');
      window.location.reload();
    });
  }
}
